import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Search } from 'lucide-react';
import axios from 'axios';

export default function UserManager() {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (role) params.set('role', role);
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchUsers(); }, [page, role]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchUsers(); };

  const roleColors = { student: '#FF6B35', parent: '#1B4F8A', admin: '#06D6A0', teacher: '#FFD23F' };

  return (
    <div className="min-h-screen" style={{ background: '#FFF8F0' }}>
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <button onClick={() => navigate('/admin')}><ArrowLeft size={22} color="#1B4F8A" /></button>
        <h1 className="text-xl font-extrabold flex-1" style={{ color: '#1B4F8A' }}>Users ({total})</h1>
      </div>

      {/* Filters */}
      <div className="px-4 space-y-2 mb-3">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-3" style={{ color: '#8B7355' }} />
            <input data-testid="user-search-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, email..."
              className="w-full bg-white border-2 rounded-xl pl-9 pr-3 py-2 text-xs font-bold outline-none" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
          </div>
          <button type="submit" className="px-4 py-2 rounded-xl text-xs font-bold text-white" style={{ background: '#1B4F8A' }}>Search</button>
        </form>
        <div className="flex gap-2">
          {['', 'student', 'parent', 'admin'].map(r => (
            <button key={r} onClick={() => { setRole(r); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${role === r ? 'text-white' : ''}`}
              style={{
                borderColor: r ? roleColors[r] : '#E5DACE',
                background: role === r ? (r ? roleColors[r] : '#1B4F8A') : 'white',
                color: role === r ? 'white' : '#1B4F8A'
              }}>
              {r ? r.charAt(0).toUpperCase() + r.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Users List */}
      <div className="px-4 space-y-2 pb-8">
        {users.map(u => (
          <div key={u.user_id} className="bg-white border-2 rounded-2xl p-3 flex items-center gap-3" style={{ borderColor: '#E5DACE' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold" style={{ background: '#F0E6D8' }}>
              {u.avatar_url || u.name?.charAt(0) || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" style={{ color: '#1B4F8A' }}>{u.name}</p>
              <p className="text-xs" style={{ color: '#8B7355' }}>{u.phone || u.email}</p>
            </div>
            <span className="px-2 py-1 rounded-full text-xs font-bold" style={{ background: (roleColors[u.role] || '#8B7355') + '20', color: roleColors[u.role] || '#8B7355' }}>
              {u.role}
            </span>
          </div>
        ))}
        {total > 20 && (
          <div className="flex justify-center gap-2 pt-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-4 py-2 rounded-full text-xs font-bold border-2" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>Prev</button>
            <span className="px-4 py-2 text-xs font-bold" style={{ color: '#8B7355' }}>Page {page}</span>
            <button onClick={() => setPage(p => p + 1)} className="px-4 py-2 rounded-full text-xs font-bold border-2" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
